import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { setUserToken, userApi, formatApiErrorDetail, useUserAuth } from "../lib/UserAuthContext";
import { LOGO_URL } from "../lib/site";

const PENDING_KEY = "cashpark_pending_email";
const DEV_CODE_KEY = "cashpark_dev_code";

export default function VerifyOtp() {
  const nav = useNavigate();
  const { refresh } = useUserAuth();
  const [email] = useState(() => sessionStorage.getItem(PENDING_KEY) || "");
  const [devCode] = useState(() => sessionStorage.getItem(DEV_CODE_KEY) || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!email) nav("/register", { replace: true });
  }, [email, nav]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const verify = async (finalCode) => {
    const c = (finalCode ?? code).trim();
    if (c.length !== 6) { toast.error("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      const { data } = await userApi.post("/auth/verify-otp", { email, code: c });
      if (data?.token) setUserToken(data.token);
      await refresh();
      sessionStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(DEV_CODE_KEY);
      nav("/signup-success", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    // We don't have the raw password here, so a real resend is another /register step.
    // Best action: send them back to /register with email prefilled feel — simplest UX is to prompt.
    toast.info("Please go back and re-enter your details to send a new code.");
    nav("/register");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="verify-otp-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-2 mb-8">
          <img src={LOGO_URL} alt="CashPark" className="h-14 w-14" />
          <span className="font-display font-bold text-xl text-[#0B192C]">CashPark <span className="text-[#FF6B00]">Inc</span></span>
        </Link>
        <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-orange-50 text-[#FF6B00] flex items-center justify-center">
            <ShieldCheck size={26} />
          </div>
          <h1 className="font-display text-2xl font-bold text-[#0B192C] mt-4">Confirm your email</h1>
          <p className="text-sm text-slate-500 mt-1">We sent a 6-digit code to</p>
          <p className="font-semibold text-[#0B192C] break-all" data-testid="otp-email-display">{email || "—"}</p>

          {devCode && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-left" data-testid="dev-code-hint">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-800">Test mode</div>
              <p className="text-xs text-amber-800 mt-1">
                Email service isn't verified for your domain yet, so the code is: <span className="font-mono font-bold text-lg">{devCode}</span>
              </p>
            </div>
          )}

          <div className="flex justify-center mt-6">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }}
              data-testid="otp-input"
              disabled={busy}
            >
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} data-testid={`otp-slot-${i}`} className="w-11 h-12 text-lg font-display font-bold text-[#0B192C]" />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button onClick={() => verify()} disabled={busy || code.length !== 6} data-testid="otp-submit" className="mt-6 w-full h-11 rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
            {busy ? "Verifying…" : "Verify & complete signup"}
          </Button>

          <button onClick={resend} disabled={resendCooldown > 0} data-testid="otp-resend" className="mt-4 text-xs text-slate-500 hover:text-[#FF6B00] inline-flex items-center gap-1 disabled:opacity-50">
            <RefreshCw size={12} /> {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Didn't get it? Resend"}
          </button>
        </div>
      </div>
    </div>
  );
}
